import React, { useState, useEffect } from "react";
import { analyticsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import { categoryLabel } from "../constants/categories";
import { formatMoney } from "../pos/utils/money";

/**
 * What the stock on the shelves is worth, by category. It loads on its own so
 * a slow report never holds the rest of the dashboard back.
 */
const StockValue = () => {
  const { t } = useI18n();
  const { can, shop } = useMembership();
  const warehouseOn = Boolean(shop?.warehouse_enabled);
  const [inventoryReport, setInventoryReport] = useState([]);
  const [loading, setLoading] = useState(true);
  // Stock is worth what it would sell for, or what it cost to buy: the first
  // is the shelf's takings potential, the second is the money tied up in it
  const [basis, setBasis] = useState("sell");
  // With a warehouse: what the shop holds, what the warehouse holds, or both
  const [location, setLocation] = useState("both");

  // The API withholds the buy-price figures from anyone without this, so
  // offering the choice would only show them zeros
  const canSeeCosts = can("power.see_costs");
  const activeBasis = canSeeCosts ? basis : "sell";

  const activeLocation = warehouseOn ? location : "both";

  useEffect(() => {
    analyticsAPI
      .getInventoryReport(warehouseOn ? activeLocation : undefined)
      .then((response) => setInventoryReport(response.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [warehouseOn, activeLocation]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-primary-600"></div>
      </div>
    );
  }

  if (inventoryReport.length === 0 && !warehouseOn) return null;

  const valueOf = (item) =>
    Number(
      activeBasis === "buy" ? item.inventory_cost_value : item.inventory_value,
    ) || 0;
  const total = inventoryReport.reduce((sum, item) => sum + valueOf(item), 0);
  const uncosted = inventoryReport.reduce(
    (sum, item) => sum + Number(item.uncosted_count || 0),
    0,
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          {t("dashboard.stockValue.title")}
        </h2>
        {warehouseOn && (
          <div
            role="group"
            aria-label={t("dashboard.stockValue.where")}
            className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm"
          >
            {["shop", "warehouse", "both"].map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={location === key}
                onClick={() => setLocation(key)}
                className={`px-4 py-2 font-medium ${
                  location === key
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t(`dashboard.stockValue.locations.${key}`)}
              </button>
            ))}
          </div>
        )}
        {canSeeCosts && (
          <div
            role="group"
            aria-label={t("dashboard.stockValue.valueBy")}
            className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm"
          >
            {[
              ["sell", "dashboard.stockValue.sellPrice"],
              ["buy", "dashboard.stockValue.buyPrice"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={basis === key}
                onClick={() => setBasis(key)}
                className={`px-4 py-2 font-medium ${
                  basis === key
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t(label)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6 rounded-lg bg-primary-50 px-4 py-3 flex items-center justify-between gap-3">
        <span className="font-medium text-gray-700">
          {t("dashboard.stockValue.total")}
          {canSeeCosts && (
            <>
              {" · "}
              {t(
                activeBasis === "buy"
                  ? "dashboard.stockValue.buyPrice"
                  : "dashboard.stockValue.sellPrice",
              )}
            </>
          )}
        </span>
        <span className="text-xl font-bold text-primary-700">
          {formatMoney(total)}
        </span>
      </div>

      {activeBasis === "buy" && uncosted > 0 && (
        <p className="mb-4 text-sm text-amber-700">
          {t(
            uncosted === 1
              ? "dashboard.stockValue.uncosted"
              : "dashboard.stockValue.uncostedPlural",
            { count: uncosted },
          )}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {inventoryReport.map((item) => (
          <div key={item.category} className="border rounded-lg p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">
              {categoryLabel(item.category)}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">
                  {t("dashboard.stockValue.products")}
                </span>
                <span className="font-semibold">{item.product_count}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">
                  {t("dashboard.stockValue.totalStock")}
                </span>
                <span className="font-semibold">{item.total_stock}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">
                  {t("dashboard.stockValue.value")}
                </span>
                <span className="font-bold text-primary-600">
                  {formatMoney(valueOf(item))}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StockValue;
